import {
  ACTION,
  AggregationType,
  Alert,
  AlertVariants,
  AppwideDrawerContentType,
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  ConnectionType,
  ContextGuideContext,
  CurrencySelect,
  Dialog,
  DrawerContent,
  DrawerFooter,
  EMPTY_ARRAY,
  EmptyMessage,
  EmptyView,
  Flex,
  FormControlSizes,
  FormGroup,
  FormSection,
  HStack,
  HedgeControlStatusEnum,
  Icon,
  IconName,
  InlineFormattedNumber,
  LoaderTalos,
  MixpanelEvent,
  MixpanelEventSource,
  MixpanelSourceProvider,
  ModeEnum,
  NotificationVariants,
  NumberInput,
  SearchSelect,
  SectionTitle,
  Text,
  Toasts,
  VStack,
  setAlpha,
  useAppwideDrawerContext,
  useAssetsContext,
  useCurrenciesContext,
  useDisclosure,
  useDynamicCallback,
  useGlobalToasts,
  useMixpanel,
  useObservableValue,
  useSecuritiesContext,
  useStrategiesContext,
  useToasts,
  type Aggregation,
  type AggregationWithAccounts,
  type Currency,
  type IHedgeRule,
  type ISubaccount,
  type OrderStrategy,
  type Security,
} from '@talos/kyoko';
import { getFormGroupMarginBottom } from '@talos/kyoko/src/components/Form/FormGroup/styles';
import Big from 'big.js';
import { flatten } from 'lodash-es';
import { useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { map, of } from 'rxjs';
import styled, { useTheme } from 'styled-components';
import { DRAWER_FOOTER_HEIGHT } from '../../../../../packages/kyoko/src/components/Drawer/tokens';
import { getCrossAssetRiskGroupAssetsDisplayValue } from '../../containers/Admin/TradingLimits/CrossAssetRiskGroups/useCrossAssetRiskGroupColumns';
import {
  useGetCrossAssetRiskGroupsQuery,
  type CrossAssetRiskGroup,
} from '../../containers/Admin/TradingLimits/SubAccountTradingLimits/crossAssetRiskGroupsApi';
import { getCrossAssetRiskGroupLabel } from '../../containers/Admin/TradingLimits/SubAccountTradingLimits/matchTargetUtils';
import {
  useHedgeRuleCreationMutation,
  useHedgeRuleDeletionMutation,
  useHedgeRuleModificationMutation,
  useHedgeRuleQuery,
} from '../../containers/Trading/Markets/PositionAutoHedgingRules/queries';
import { getHedgeRuleDeleteItemName } from '../../containers/Trading/Markets/PositionAutoHedgingRules/useDeleteHedgeRule';
import { useDeleteConfirmation, useFeatureFlag, useRoleAuth } from '../../hooks';
import { useAggregationsQuery } from '../../hooks/useAggregationRequests';
import { useSubAccountRollupMemberships, useSubAccounts } from '../../providers';
import { useHedgePositionStatus, type IHedgePositionStatusRow } from '../../providers/HedgePositionStatusProvider';
import { AggregationsSelector } from '../AggregationsSelector';
import { isActiveCrossAssetRiskGroup } from '../AutoHedging/crossAssetRiskGroup';
import type { HedgeRulePatchPayload, HedgeRulePostPayload } from '../AutoHedging/types';
import { useNavigateToHedgeRulesSetup } from '../AutoHedging/useNavigateToHedgeRulesSetup';
import { GuideButton } from '../GuideButton';
import { ToggleParameter } from '../OMS/Parameters/ToggleParameter';
import { SymbolSelector } from '../SymbolSelector';
import { isAllAssetsHedgeGroup } from './hedgeSymbol';
import { PositionAutoHedgingCard, useCurrentPosition } from './PositionAutoHedgingCard';
import { PositionAutoHedgingStatus, TalosToolsPositionControlActions } from './PositionAutoHedgingStatus';
import {
  getInitialState,
  init,
  reducer,
  setHedgeRule,
  touchAll,
  updateField,
  updateMode,
  usePositionAutoHedgingDrawerDependencies,
} from './reducer';
import type { EditIHedgeRuleForm, HedgeMonitoredAsset, InitReducerPayload } from './types';
import {
  CROSS_ASSET_RISK_GROUP_OPTIONAL_PLACEHOLDER,
  CROSS_ASSET_RISK_GROUP_UNAVAILABLE_PLACEHOLDER,
  selectCrossAssetRiskGroupIsResolved,
  selectFormHasChanged,
  selectFormIsValid,
  selectFormValues,
  selectFormValuesValid,
  selectGuideCrossAssetRiskGroupLabel,
  selectIsHedgeRuleOutOfSync,
  selectPATCHPayloadFromHedgeRule,
} from './utils';

export const usePositionAutoHedgingRulesDrawer = () => {
  const mixpanel = useMixpanel();
  const { openDrawer } = useAppwideDrawerContext();

  const handleOnClickOpenRules = useNavigateToHedgeRulesSetup();

  const openPositionAutoHedgingRulesDrawer = useDynamicCallback(
    (payload?: { hedgeRuleID: IHedgeRule['HedgeRuleID'] }) => {
      mixpanel.track(MixpanelEvent.OpenAutoHedgePositionRuleDrawer);
      openDrawer({
        type: AppwideDrawerContentType.AutoHedgePositionRule,
        title: 'Position Autohedging Rule',
        renderHeaderContent: () => (
          <HStack gap="spacing4">
            <Button ghost size={FormControlSizes.Small} onClick={handleOnClickOpenRules}>
              <HStack color="colorTextSubtle" gap="spacing2">
                <Icon size={12} icon={IconName.ExternalLink} />
                <Text>Open Rules</Text>
              </HStack>
            </Button>
            <GuideButton guideID="auto-hedge-position-rule" />
          </HStack>
        ),
        renderContent: () => (
          <MixpanelSourceProvider value={MixpanelEventSource.PositionAutoHedgingDrawer}>
            <DrawerLoaderWrapper hedgeRuleID={payload?.hedgeRuleID} />
          </MixpanelSourceProvider>
        ),
      });
    }
  );

  return {
    openPositionAutoHedgingRulesDrawer,
  };
};

type DrawerLoaderWrapperProps = {
  hedgeRuleID?: IHedgeRule['HedgeRuleID'];
};

function DrawerLoaderWrapper({ hedgeRuleID: initialHedgeRuleID, ...rest }: DrawerLoaderWrapperProps) {
  // Keep track of the current hedge rule ID, this will change from undefined to the actual ID once the a rule is created.
  const [hedgeRuleID, setHedgeRuleID] = useState<string | undefined>(initialHedgeRuleID);
  const { closeDrawer } = useAppwideDrawerContext();

  const hedgeRuleQuery = useHedgeRuleQuery(hedgeRuleID);
  const aggregationsQuery = useAggregationsQuery();
  const userAggregations = useMemo(
    () => aggregationsQuery.data?.filter(agg => agg.AggregationType === AggregationType.User),
    [aggregationsQuery.data]
  );

  const { hedgePositionStatusObs } = useHedgePositionStatus();
  const hedgePositionStatus = useObservableValue(
    // Since this runs after the initial render, we deduce if it is loading based on if the hedgePositionStatus
    // is null or status | undefined. Null means it is loading, undefined means it is not found.
    // all a workaround to ensure we don't get jumping jsx.
    () => {
      const hedgeStatus = hedgePositionStatusObs.pipe(map(json => (hedgeRuleID ? json.get(hedgeRuleID) : undefined)));
      if (hedgeStatus) {
        return hedgeStatus;
      }

      return of(null);
    },
    [hedgePositionStatusObs, hedgeRuleID],
    null
  );

  const isHedgePositionStatusLoading = hedgePositionStatus === null;
  const isHedgeRuleQueryLoading = !!hedgeRuleID && hedgeRuleQuery.isPending;
  // Empty/404 responses resolve as success with `null` (see useHedgeRuleQuery); network failures use isError.
  const isHedgeRuleMissing =
    !!hedgeRuleID && !isHedgeRuleQueryLoading && (hedgeRuleQuery.isError || hedgeRuleQuery.data == null);

  if (isHedgeRuleQueryLoading || aggregationsQuery.isLoading) {
    return (
      <DrawerContent h="100%">
        <LoaderTalos />
      </DrawerContent>
    );
  }

  if (isHedgeRuleMissing) {
    return (
      <DrawerContent h="100%">
        <EmptyView data-testid="hedge-rule-not-found">
          <EmptyMessage>This hedge rule could not be found. It may have been deleted.</EmptyMessage>
          <Button variant={ButtonVariants.Primary} onClick={closeDrawer}>
            Close
          </Button>
        </EmptyView>
      </DrawerContent>
    );
  }

  if (isHedgePositionStatusLoading) {
    return (
      <DrawerContent h="100%">
        <LoaderTalos />
      </DrawerContent>
    );
  }

  return (
    <>
      <AutoHedgingRulesDrawer
        key={hedgeRuleID}
        hedgeRuleID={hedgeRuleID}
        aggregations={userAggregations ?? EMPTY_ARRAY}
        hedgePositionStatus={hedgePositionStatus}
        setHedgeRuleID={setHedgeRuleID}
        {...rest}
      />
    </>
  );
}

type AutoHedgingRulesDrawerProps = {
  hedgeRuleID: string | undefined;
  aggregations: AggregationWithAccounts[];
  hedgePositionStatus: IHedgePositionStatusRow | undefined;
  setHedgeRuleID: (hedgeRuleID: string) => void;
};

const AutoHedgerValidationLevel = {
  BUTTON: 0,
  POSITION_THRESHOLD_MODAL: 1,
} as const;
type AutoHedgerValidationLevel = (typeof AutoHedgerValidationLevel)[keyof typeof AutoHedgerValidationLevel];

function AutoHedgingRulesDrawer({
  aggregations,
  hedgeRuleID,
  hedgePositionStatus,
  setHedgeRuleID,
}: AutoHedgingRulesDrawerProps) {
  const { isAuthorized } = useRoleAuth();
  const { globalHedgeRule } = useHedgePositionStatus();
  const globalHedgeControlStatus = globalHedgeRule?.HedgeControlStatus;
  const { closeDrawer } = useAppwideDrawerContext();
  const { getGuide } = useContext(ContextGuideContext);
  const isGuideEnabled = getGuide('auto-hedge-position-rule');

  const mixpanel = useMixpanel();
  const toastProps = useToasts({ defaultProps: { timeout: 3000, dismissable: true } });
  const addToast = toastProps.add;
  const { currenciesList: currencies, currenciesBySymbol } = useCurrenciesContext();
  const { getAssetDisplaySymbol } = useAssetsContext();
  const { securitiesBySymbol, searchableSecurities, spotTradingPairsByBaseCurrency, spotTradingPairsByQuoteCurrency } =
    useSecuritiesContext();
  const symbolLabelGetter = useCallback(
    (symbol: string) => {
      return securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol;
    },
    [securitiesBySymbol]
  );
  const { allSubAccounts: subaccounts, subAccountsByID } = useSubAccounts();
  const { autoHedgingStrategiesList } = useStrategiesContext();
  const { rollupMembershipsByParentChild } = useSubAccountRollupMemberships();

  const { positionHedgerHomeCurrency, enableHedgeRuleCrossAssetRiskGroup, enableCrossAssetRiskGroupDeltaRiskCheck } =
    useFeatureFlag();
  const { data: crossAssetRiskGroups = EMPTY_ARRAY } = useGetCrossAssetRiskGroupsQuery(undefined, {
    skip: !enableHedgeRuleCrossAssetRiskGroup,
  });
  const hedgeRuleQuery = useHedgeRuleQuery(hedgeRuleID);
  const queryHedgeRule = hedgeRuleQuery.data ?? undefined;

  const [state, dispatch] = useReducer(
    reducer,
    {
      hedgeRule: queryHedgeRule,
      currencies,
      strategies: autoHedgingStrategiesList,
      currenciesBySymbol,
      subaccounts,
      aggregations,
      rollupMembershipsByParentChild,
      spotTradingPairsByBaseCurrency,
      spotTradingPairsByQuoteCurrency,
      securitiesBySymbol,
      searchableSecurities,
      positionHedgerHomeCurrency,
      crossAssetRiskGroups,
      enableHedgeRuleCrossAssetRiskGroup,
      enableCrossAssetRiskGroupDeltaRiskCheck,
    } satisfies InitReducerPayload,
    initialArg => reducer(getInitialState(initialArg), init(initialArg))
  );

  const hedgeRuleBeingChanged = state.hedgeRuleBeingChanged ?? queryHedgeRule;

  usePositionAutoHedgingDrawerDependencies({
    dispatch,
    currencies,
    currenciesBySymbol,
    strategies: autoHedgingStrategiesList,
    subaccounts,
    aggregations,
    rollupMembershipsByParentChild,
    spotTradingPairsByBaseCurrency,
    spotTradingPairsByQuoteCurrency,
    securitiesBySymbol,
    searchableSecurities,
    positionHedgerHomeCurrency,
    crossAssetRiskGroups,
    enableHedgeRuleCrossAssetRiskGroup,
    enableCrossAssetRiskGroupDeltaRiskCheck,
  });

  const handleOnChangeGetter = useMemo(() => {
    return {
      Asset: (value?: HedgeMonitoredAsset) => dispatch(updateField({ key: 'Asset', value })),
      TargetAsset: (value?: Currency) => dispatch(updateField({ key: 'TargetAsset', value })),
      TargetAccount: (value?: ISubaccount) => dispatch(updateField({ key: 'TargetAccount', value })),
      HedgeStrategy: (value?: OrderStrategy) => dispatch(updateField({ key: 'HedgeStrategy', value })),
      LongPositionLimit: (value?: string) => dispatch(updateField({ key: 'LongPositionLimit', value })),
      ShortPositionLimit: (value?: string) => dispatch(updateField({ key: 'ShortPositionLimit', value })),
      TargetPosition: (value?: string) => dispatch(updateField({ key: 'TargetPosition', value })),
      AccountID: (value?: ISubaccount) => dispatch(updateField({ key: 'AccountID', value })),
      HedgeAggregation: (value?: AggregationWithAccounts) => dispatch(updateField({ key: 'HedgeAggregation', value })),
      CrossAssetRiskGroup: (value?: CrossAssetRiskGroup) =>
        dispatch(updateField({ key: 'CrossAssetRiskGroup', value })),
      HedgeSymbol: (value?: Security) => dispatch(updateField({ key: 'HedgeSymbol', value })),
    } satisfies Record<keyof EditIHedgeRuleForm, () => void>;
  }, [dispatch]);

  const handleHedgeSecurityChanged = useCallback(
    (symbol?: string) => {
      const security = symbol ? state.form.HedgeSymbol.availableItems.find(item => item.Symbol === symbol) : undefined;
      handleOnChangeGetter.HedgeSymbol(security);
    },
    [handleOnChangeGetter, state.form.HedgeSymbol.availableItems]
  );

  const formIsValid = selectFormIsValid(state);
  const hasFormChanged = selectFormHasChanged(state);
  const crossAssetRiskGroupIsResolved = selectCrossAssetRiskGroupIsResolved(state);
  const guideCrossAssetRiskGroupLabel = selectGuideCrossAssetRiskGroupLabel(state);
  const existingCrossAssetRiskGroupName = hedgeRuleBeingChanged?.CrossAssetRiskGroup;
  const isCrossAssetRiskGroupUnavailable =
    isActiveCrossAssetRiskGroup(existingCrossAssetRiskGroupName) &&
    state.form.CrossAssetRiskGroup.value?.Name !== existingCrossAssetRiskGroupName;
  const crossAssetRiskGroupPlaceholder = isCrossAssetRiskGroupUnavailable
    ? CROSS_ASSET_RISK_GROUP_UNAVAILABLE_PLACEHOLDER
    : hedgeRuleBeingChanged
      ? ''
      : CROSS_ASSET_RISK_GROUP_OPTIONAL_PLACEHOLDER;

  const canSubmit =
    formIsValid && hasFormChanged && crossAssetRiskGroupIsResolved && isAuthorized(ACTION.EDIT_AUTOHEDGING);

  const onChangeMode = useCallback(
    (mode: ModeEnum) => {
      dispatch(updateMode(mode));
    },
    [dispatch]
  );

  const createHedgeRuleMutation = useHedgeRuleCreationMutation();
  const modifyHedgeRuleMutation = useHedgeRuleModificationMutation();
  const deleteHedgeRuleMutation = useHedgeRuleDeletionMutation();
  const { confirmDelete } = useDeleteConfirmation();
  const { add: addGlobalToast } = useGlobalToasts();

  const isMutating =
    createHedgeRuleMutation.isPending || modifyHedgeRuleMutation.isPending || deleteHedgeRuleMutation.isPending;
  const isLoading = isMutating || hedgeRuleQuery.isLoading;
  const isCurrentlyHedging = hedgePositionStatus?.HedgeControlStatus === HedgeControlStatusEnum.Hedging;
  const canDelete = !!hedgeRuleID && isAuthorized(ACTION.EDIT_AUTOHEDGING);

  const onCreateHedgeRule = useCallback(
    async (newHedgeRule: HedgeRulePostPayload & { Mode: ModeEnum }) => {
      return createHedgeRuleMutation
        .mutateAsync(newHedgeRule)
        .then(hedgeRule => {
          dispatch(setHedgeRule(hedgeRule));
          addToast({
            text: 'Hedge Rule created.',
            variant: NotificationVariants.Positive,
          });
          setHedgeRuleID(hedgeRule.HedgeRuleID);
        })
        .catch(error => {
          addToast({
            text: error?.toString() || 'Failed to create.',
            variant: NotificationVariants.Negative,
          });
        });
    },
    [createHedgeRuleMutation, addToast, setHedgeRuleID]
  );

  const onModifyHedgeRule = useCallback(
    async (hedgeRule: HedgeRulePatchPayload) => {
      return modifyHedgeRuleMutation
        .mutateAsync({ id: hedgeRule.HedgeRuleID, payload: hedgeRule })
        .then(hedgeRule => {
          dispatch(setHedgeRule(hedgeRule));
          addToast({
            text: 'Hedge rule updated.',
            variant: NotificationVariants.Positive,
          });
          setHedgeRuleID(hedgeRule.HedgeRuleID);
        })
        .catch(error => {
          addToast({
            text: error?.toString() || 'Failed to update.',
            variant: NotificationVariants.Negative,
          });
        });
    },
    [modifyHedgeRuleMutation, addToast, setHedgeRuleID]
  );

  const handleDeleteHedgeRule = useCallback(() => {
    if (!queryHedgeRule) {
      return;
    }
    const rollupName = subAccountsByID.get(queryHedgeRule.AccountID)?.Name;
    confirmDelete({
      title: 'Delete Hedge Rule',
      itemName: getHedgeRuleDeleteItemName(queryHedgeRule, rollupName),
      onConfirm: () => {
        mixpanel.track(MixpanelEvent.ClickDeleteHedgeRule);
        deleteHedgeRuleMutation
          .mutateAsync(queryHedgeRule.HedgeRuleID)
          .then(() => {
            addGlobalToast({
              text: 'Hedge rule deleted.',
              variant: NotificationVariants.Positive,
            });
            closeDrawer();
          })
          .catch(error => {
            addGlobalToast({
              text: error?.toString() || 'Failed to delete.',
              variant: NotificationVariants.Negative,
            });
          });
      },
    });
  }, [addGlobalToast, closeDrawer, confirmDelete, deleteHedgeRuleMutation, mixpanel, queryHedgeRule, subAccountsByID]);

  const { position: convertedPosition, source } = useCurrentPosition({
    account: state.form.AccountID.value?.Name,
    asset: state.form.Asset.value?.Symbol,
    targetAsset: state.form.TargetAsset.value?.Symbol,
    hedgePositionStatus,
  });

  const hedgeRuleChangedWarningDialog = useDisclosure();

  const handleSubmitHedgeRule = useCallback(
    async (level: AutoHedgerValidationLevel) => {
      const enableHedgeRuleCrossAssetRiskGroup = state.referenceData.enableHedgeRuleCrossAssetRiskGroup;
      const hedgeRuleForm = selectFormValues(state.form, enableHedgeRuleCrossAssetRiskGroup);
      const isValid = selectFormValuesValid(hedgeRuleForm);
      if (!isValid || !hasFormChanged || !crossAssetRiskGroupIsResolved) {
        return;
      }
      const isCurrentlyHedging = hedgePositionStatus?.HedgeControlStatus === HedgeControlStatusEnum.Hedging;
      const isCurrentlyDisabled = hedgePositionStatus?.HedgeControlStatus === HedgeControlStatusEnum.Disabled;

      const isGlobalHedgingDisabled =
        globalHedgeControlStatus === HedgeControlStatusEnum.Disabled ||
        globalHedgeControlStatus === HedgeControlStatusEnum.Error;

      const checkPositionValidation = !isGlobalHedgingDisabled && !isCurrentlyHedging && !isCurrentlyDisabled;

      if (level <= AutoHedgerValidationLevel.BUTTON && convertedPosition && checkPositionValidation) {
        /** if the converted position breaches short/long thresholds we open a dialog that requires user confirmation */
        const isShortThresholdBreached = Big(Big(hedgeRuleForm.ShortPositionLimit).mul(-1)).gte(convertedPosition);
        const isLongThresholdBreached = Big(convertedPosition).gte(hedgeRuleForm.LongPositionLimit);

        if (isShortThresholdBreached || isLongThresholdBreached) {
          hedgeRuleChangedWarningDialog.open();
          return;
        }
      }

      if (!state.hedgeRuleBeingChanged) {
        mixpanel.track(MixpanelEvent.ClickCreateHedgeRule);
        await onCreateHedgeRule({ ...hedgeRuleForm, Mode: state.command.mode });
      } else {
        const newHedgeRule = {
          ...selectPATCHPayloadFromHedgeRule(state.hedgeRuleBeingChanged, enableHedgeRuleCrossAssetRiskGroup),
          ...hedgeRuleForm,
        };
        mixpanel.track(MixpanelEvent.ClickUpdateHedgeRule);
        await onModifyHedgeRule(newHedgeRule);
      }
    },
    [
      state.form,
      state.hedgeRuleBeingChanged,
      state.command.mode,
      state.referenceData.enableHedgeRuleCrossAssetRiskGroup,
      hasFormChanged,
      crossAssetRiskGroupIsResolved,
      hedgePositionStatus?.HedgeControlStatus,
      globalHedgeControlStatus,
      convertedPosition,
      hedgeRuleChangedWarningDialog,
      mixpanel,
      onCreateHedgeRule,
      onModifyHedgeRule,
    ]
  );

  const limitError = useMemo(() => {
    const arr = flatten([
      state.form.ShortPositionLimit.errorString.split(', '),
      state.form.TargetPosition.errorString.split(', '),
      state.form.LongPositionLimit.errorString.split(', '),
    ]).filter(Boolean);
    return arr.at(0);
  }, [
    state.form.ShortPositionLimit.errorString,
    state.form.TargetPosition.errorString,
    state.form.LongPositionLimit.errorString,
  ]);

  const hedgeSymbol = state.form.HedgeSymbol.value?.Symbol;

  const hedgeRuleOutOfSync =
    hedgeRuleBeingChanged &&
    queryHedgeRule &&
    // We only want to show the out of sync message when we got the latest hedge rule from backend.
    !hedgeRuleQuery.isFetching &&
    /* Its not sufficient to only check the Revision here, since the revision is updated when enabling/disabling the rule */
    selectIsHedgeRuleOutOfSync({ hedgeRuleBeingChanged: hedgeRuleBeingChanged, latestHedgeRule: queryHedgeRule });

  const theme = useTheme();

  return (
    <>
      <DrawerContent p="0" h="100%" position="relative" overflow="auto" gridTemplateRows="min-content auto min-content">
        {isLoading && <LoadingOverlay />}

        {hedgeRuleBeingChanged && isAuthorized(ACTION.HEDGER_SYNCHRONIZATION) && (
          <VStack gap="spacing4" alignSelf="center">
            <TalosToolsPositionControlActions addToast={addToast} hedgeRule={hedgeRuleBeingChanged} />
          </VStack>
        )}
        <FormSection>
          {!hedgeRuleBeingChanged && (
            <>
              <FormGroup inline label="Enabled" alignItems="center" data-testid="mode-toggle" mb="spacing12">
                <ToggleParameter
                  off={ModeEnum.Disabled}
                  on={ModeEnum.Enabled}
                  onChange={onChangeMode}
                  value={state.command.mode}
                  size={FormControlSizes.Default}
                />
              </FormGroup>
              {isGuideEnabled && (
                <FormGroup data-testid="guide-mode">
                  <Alert variant={AlertVariants.Guide} p="spacing8" dismissable={false}>
                    <Text color="colorTextDefault">
                      {state.command.mode === ModeEnum.Enabled ? (
                        <>
                          The rule will start as <Text color="colorTextImportant">Enabled</Text>. The hedger will
                          monitor the position and make hedging decisions.
                        </>
                      ) : (
                        <>
                          The rule will start as <Text color="colorTextImportant">Disabled</Text>. The hedger will
                          monitor the position but not make any hedging decisions.
                        </>
                      )}
                    </Text>
                  </Alert>
                </FormGroup>
              )}
            </>
          )}

          {hedgeRuleOutOfSync && (
            <FormGroup>
              <Alert
                data-testid="hedge-rule-out-of-sync"
                variant={AlertVariants.Notice}
                dismissable={false}
                alignItems="center"
                p="spacing8"
              >
                The rule has been updated. Refresh to see the latest changes.
                <Button
                  data-testid="refresh-hedge-rule-button"
                  size={FormControlSizes.Tiny}
                  onClick={() => {
                    dispatch(setHedgeRule(queryHedgeRule));
                  }}
                >
                  Refresh
                </Button>
              </Alert>
            </FormGroup>
          )}

          {hedgeRuleBeingChanged && hedgePositionStatus && (
            <FormGroup>
              <PositionAutoHedgingStatus
                hasFormChanged={hasFormChanged}
                hedgePositionStatus={hedgePositionStatus}
                hedgeRule={hedgeRuleBeingChanged}
                // Prioritize the mode from the wire if exists.
                mode={queryHedgeRule?.Mode ?? hedgeRuleBeingChanged.Mode}
                addToast={addToast}
              />
            </FormGroup>
          )}

          <FormGroup>
            <PositionAutoHedgingCard
              form={state.form}
              position={convertedPosition}
              source={source}
              hedgePositionStatus={hedgePositionStatus}
              hedgeRule={hedgeRuleBeingChanged}
              // Prioritize the mode from the wire if exists.
              mode={queryHedgeRule?.Mode ?? state.command.mode}
            />
          </FormGroup>

          <SectionTitle showNumber mb="spacing12" title="Monitoring" />

          <Flex gap="spacing12">
            <FormGroup label="Monitored Rollup" error={state.form.AccountID.errorString} flex={1}>
              <SearchSelect
                readOnly={state.form.AccountID.isDisabled}
                data-testid="monitored-rollup-id"
                disabled={state.form.AccountID.isDisabled}
                touched={state.form.AccountID.isTouched}
                invalid={state.form.AccountID.hasError}
                selection={state.form.AccountID.value}
                options={state.form.AccountID.availableItems}
                onChange={handleOnChangeGetter['AccountID']}
                maxHeight={300}
                dropdownPlacement="bottom-start"
                getGroup={getSubaccountGroup}
                getLabel={getSubaccountLabel}
                initialSortByLabel={false}
              />
            </FormGroup>
            <FormGroup label="Monitored Asset" error={state.form.Asset.errorString} flex={1} mb="18px">
              <SearchSelect
                data-testid="asset"
                readOnly={state.form.Asset.isDisabled}
                disabled={state.form.Asset.isDisabled}
                touched={state.form.Asset.isTouched}
                invalid={state.form.Asset.hasError}
                selection={state.form.Asset.value}
                options={state.form.Asset.availableItems}
                onChange={handleOnChangeGetter['Asset']}
                getLabel={asset =>
                  'DisplaySymbol' in asset && asset.DisplaySymbol ? asset.DisplaySymbol : asset.Symbol
                }
                getDescription={asset => asset.Description}
                maxHeight={300}
                dropdownPlacement="bottom-start"
              />
            </FormGroup>
          </Flex>

          {enableHedgeRuleCrossAssetRiskGroup && (
            <FormGroup
              label="Cross-Asset Risk Group"
              error={state.form.CrossAssetRiskGroup.errorString}
              data-testid="cross-asset-risk-group-form-group"
            >
              <SearchSelect
                data-testid="cross-asset-risk-group"
                readOnly={state.form.CrossAssetRiskGroup.isDisabled}
                disabled={state.form.CrossAssetRiskGroup.isDisabled}
                touched={state.form.CrossAssetRiskGroup.isTouched}
                invalid={state.form.CrossAssetRiskGroup.hasError}
                selection={state.form.CrossAssetRiskGroup.value}
                options={state.form.CrossAssetRiskGroup.availableItems}
                onChange={handleOnChangeGetter['CrossAssetRiskGroup']}
                getLabel={getCrossAssetRiskGroupLabel}
                getDescription={group =>
                  getCrossAssetRiskGroupAssetsDisplayValue(
                    group.Assets,
                    getAssetDisplaySymbol,
                    enableCrossAssetRiskGroupDeltaRiskCheck
                  ).join(', ')
                }
                showDescriptionInButton
                placeholder={crossAssetRiskGroupPlaceholder}
                maxHeight={300}
                dropdownPlacement="bottom-start"
              />
            </FormGroup>
          )}

          {isGuideEnabled && (
            <FormGroup data-testid="guide-monitor">
              <Alert variant={AlertVariants.Guide} p="spacing8" dismissable={false}>
                <Text color="colorTextDefault">
                  The hedger monitors the{' '}
                  <Text color="colorTextImportant">{state.form.Asset.value?.Symbol ?? 'Monitored Asset'}</Text> position
                  in{' '}
                  <Text color="colorTextImportant">
                    {state.form.AccountID.value ? getSubaccountLabel(state.form.AccountID.value) : 'Monitored Rollup'}
                  </Text>
                  .
                  {guideCrossAssetRiskGroupLabel && (
                    <>
                      {' '}
                      The <Text color="colorTextImportant">
                        {state.form.Asset.value?.Symbol ?? 'Monitored Asset'}
                      </Text>{' '}
                      position is aggregated across all assets in the{' '}
                      <Text color="colorTextImportant">{guideCrossAssetRiskGroupLabel}</Text> cross-asset risk group.
                    </>
                  )}
                </Text>
              </Alert>
            </FormGroup>
          )}

          <SectionTitle showNumber mb="spacing12" title="Thresholds" />
          <FormGroup
            label="Target Asset"
            error={state.form.TargetAsset.errorString}
            warning={state.form.TargetAsset.warningString}
            data-testid="target-asset-form-group"
          >
            <CurrencySelect
              data-testid="target-asset"
              disabled={state.form.TargetAsset.isDisabled}
              touched={state.form.TargetAsset.isTouched}
              invalid={state.form.TargetAsset.hasError}
              value={state.form.TargetAsset.value}
              options={state.form.TargetAsset.availableItems}
              onChange={handleOnChangeGetter['TargetAsset']}
            />
          </FormGroup>
          <FormGroup error={limitError} data-testid="limit-form-group">
            <Flex gap="spacing12">
              <FormGroup label="Short Threshold" mb="0" flex={1}>
                <NumberInput
                  data-testid="short-threshold"
                  placeholder="500"
                  disabled={state.form.ShortPositionLimit.isDisabled}
                  invalid={state.form.ShortPositionLimit.hasError}
                  value={state.form.ShortPositionLimit.displayValue}
                  onChange={handleOnChangeGetter['ShortPositionLimit']}
                  suffix={state.form.TargetAsset.value?.Symbol}
                  touched={state.form.ShortPositionLimit.isTouched}
                  min="0"
                />
              </FormGroup>
              <FormGroup label="Target Position" mb="0" flex={1}>
                <NumberInput
                  data-testid="target-position"
                  placeholder="-200"
                  disabled={state.form.TargetPosition.isDisabled}
                  invalid={state.form.TargetPosition.hasError}
                  value={state.form.TargetPosition.displayValue}
                  onChange={handleOnChangeGetter['TargetPosition']}
                  suffix={state.form.TargetAsset.value?.Symbol}
                  touched={state.form.TargetPosition.isTouched}
                />
              </FormGroup>
              <FormGroup label="Long Threshold" mb="0" flex={1}>
                <NumberInput
                  data-testid="long-threshold"
                  placeholder="700"
                  disabled={state.form.LongPositionLimit.isDisabled}
                  invalid={state.form.LongPositionLimit.hasError}
                  value={state.form.LongPositionLimit.displayValue}
                  onChange={handleOnChangeGetter['LongPositionLimit']}
                  suffix={state.form.TargetAsset.value?.Symbol}
                  touched={state.form.LongPositionLimit.isTouched}
                  min="0"
                />
              </FormGroup>
            </Flex>
          </FormGroup>

          {isGuideEnabled && (
            <FormGroup data-testid="guide-thresholds">
              <Alert variant={AlertVariants.Guide} p="spacing8" dismissable={false}>
                <Text color="colorTextDefault">
                  When the short position exceeds{' '}
                  <Text color="colorTextImportant">
                    {state.form.ShortPositionLimit.value && state.form.TargetAsset.value ? (
                      <InlineFormattedNumber
                        number={state.form.ShortPositionLimit.value}
                        currency={state.form.TargetAsset.value.Symbol}
                        round
                        trimTrailingZeroes
                      />
                    ) : (
                      'Short Threshold'
                    )}
                  </Text>{' '}
                  or the long position exceeds{' '}
                  <Text color="colorTextImportant">
                    {state.form.LongPositionLimit.value && state.form.TargetAsset.value ? (
                      <InlineFormattedNumber
                        number={state.form.LongPositionLimit.value}
                        currency={state.form.TargetAsset.value.Symbol}
                        round
                        trimTrailingZeroes
                      />
                    ) : (
                      'Long Threshold'
                    )}
                  </Text>
                  . The hedger will place an order to close the position to{' '}
                  <Text color="colorTextImportant">
                    {state.form.TargetPosition.value && state.form.TargetAsset.value ? (
                      <InlineFormattedNumber
                        number={state.form.TargetPosition.value}
                        currency={state.form.TargetAsset.value.Symbol}
                        round
                        trimTrailingZeroes
                      />
                    ) : (
                      'Target Position'
                    )}
                    .
                  </Text>
                </Text>
              </Alert>
            </FormGroup>
          )}

          <SectionTitle showNumber mb="spacing12" title="Action" />

          <FormGroup
            data-testid="hedge-symbol-form-group"
            label="Hedge Security"
            required={state.form.HedgeSymbol.isRequired}
            error={state.form.HedgeSymbol.errorString || state.form.TargetAsset.warningString}
            help={
              <>
                <Icon size={12} icon={IconName.InformationCircle} />{' '}
                {state.form.CrossAssetRiskGroup.value
                  ? isAllAssetsHedgeGroup(state.form.CrossAssetRiskGroup.value, enableCrossAssetRiskGroupDeltaRiskCheck)
                    ? 'Linear derivatives whose position currency matches the monitored asset.'
                    : 'Limited to assets in the selected Cross-Asset Risk Group.'
                  : 'Computed from the monitored and target assets.'}
              </>
            }
          >
            <SymbolSelector
              data-testid="hedge-symbol"
              symbol={state.form.HedgeSymbol.value?.Symbol}
              onSymbolChanged={handleHedgeSecurityChanged}
              availableSecurities={state.form.HedgeSymbol.availableItems}
              disabled={state.form.HedgeSymbol.isDisabled}
              readOnly={state.form.HedgeSymbol.isDisabled}
              touched={state.form.HedgeSymbol.isTouched}
              invalid={state.form.HedgeSymbol.hasError}
              showFavorites
              showGroups
              showAddMultiLegButton={false}
              maxHeight={300}
              dropdownPlacement="bottom-start"
              dropdownWidth="100%"
              w="100%"
            />
          </FormGroup>

          <FormGroup label="Hedge Account" error={state.form.TargetAccount.errorString}>
            <SearchSelect
              data-testid="hedge-account"
              disabled={state.form.TargetAccount.isDisabled}
              invalid={state.form.TargetAccount.hasError}
              touched={state.form.TargetAccount.isTouched}
              selection={state.form.TargetAccount.value}
              options={state.form.TargetAccount.availableItems}
              onChange={handleOnChangeGetter['TargetAccount']}
              maxHeight={300}
              dropdownPlacement="bottom-start"
              getGroup={getSubaccountGroup}
              getLabel={getSubaccountLabel}
              initialSortByLabel={false}
            />
          </FormGroup>

          <FormGroup label="Hedge Strategy" error={state.form.HedgeStrategy.errorString}>
            <SearchSelect
              data-testid="hedge-strategy"
              disabled={state.form.HedgeStrategy.isDisabled}
              touched={state.form.HedgeStrategy.isTouched}
              invalid={state.form.HedgeStrategy.hasError}
              selection={state.form.HedgeStrategy.value}
              options={state.form.HedgeStrategy.availableItems}
              onChange={handleOnChangeGetter['HedgeStrategy']}
              maxHeight={300}
              dropdownPlacement="bottom-start"
              getLabel={getStrategyLabel}
              getDescription={getStrategyDescription}
              initialSortByLabel={false}
            />
          </FormGroup>

          <AggregationsSelector
            symbol={hedgeSymbol}
            data-testid="hedge-aggregation"
            disabled={state.form.HedgeAggregation.isDisabled}
            touched={state.form.HedgeAggregation.isTouched}
            invalid={state.form.HedgeAggregation.hasError}
            selection={state.form.HedgeAggregation.value}
            options={state.form.HedgeAggregation.availableItems}
            onChange={handleOnChangeGetter['HedgeAggregation']}
            maxHeight={300}
            dropdownPlacement="bottom-start"
            getLabel={getAggregationLabel}
            initialSortByLabel={false}
            placeholder="Select Aggregation"
            formGroupProps={{ label: 'Hedge Aggregation', error: state.form.HedgeAggregation.errorString }}
            statusConnectionType={ConnectionType.Orders}
          />

          {isGuideEnabled && (
            <FormGroup data-testid="guide-action" mt={getFormGroupMarginBottom(theme)}>
              <Alert variant={AlertVariants.Guide} p="spacing8" dismissable={false}>
                <Text color="colorTextDefault">
                  The hedge order will be on the symbol:{' '}
                  <Text color="colorTextImportant">
                    {hedgeSymbol ? symbolLabelGetter(hedgeSymbol) : 'Hedge Security'}
                  </Text>
                  {', '}
                  use strategy{': '}
                  <Text color="colorTextImportant">
                    {state.form.HedgeStrategy.value
                      ? getStrategyLabel(state.form.HedgeStrategy.value)
                      : 'Hedge Strategy'}
                  </Text>
                  {', '}
                  aggregation{': '}
                  <Text color="colorTextImportant">
                    {state.form.HedgeAggregation.value
                      ? getAggregationLabel(state.form.HedgeAggregation.value)
                      : 'Hedge Aggregation'}
                  </Text>{' '}
                  and be booked into the sub account{': '}
                  <Text color="colorTextImportant">
                    {state.form.TargetAccount.value
                      ? getSubaccountLabel(state.form.TargetAccount.value)
                      : 'Hedge Account'}
                  </Text>
                  .
                </Text>
              </Alert>
            </FormGroup>
          )}
        </FormSection>

        <Box h={DRAWER_FOOTER_HEIGHT} />
      </DrawerContent>
      <ToastsWrapper>
        <Toasts {...toastProps} />
      </ToastsWrapper>
      <DrawerFooter position="absolute" bottom="0" left="0" right="0" background="backgroundDrawer">
        <ButtonGroup
          gap="spacing4"
          onMouseEnter={() => {
            dispatch(touchAll());
          }}
        >
          {canDelete && (
            <Button
              data-testid="delete-hedge-rule"
              variant={ButtonVariants.Negative}
              onClick={handleDeleteHedgeRule}
              disabled={isCurrentlyHedging || isMutating}
              title={isCurrentlyHedging ? 'Cannot delete: Hedge Rule has an active hedge order' : undefined}
            >
              Delete
            </Button>
          )}
          <Button variant={ButtonVariants.Default} onClick={closeDrawer}>
            Cancel
          </Button>
          <Button
            data-testid="submit-hedge-rule"
            variant={hedgeRuleID ? ButtonVariants.Primary : ButtonVariants.Positive}
            onClick={() => handleSubmitHedgeRule(AutoHedgerValidationLevel.BUTTON)}
            disabled={!canSubmit}
            flex={4}
          >
            {hedgeRuleID ? 'Update' : 'Create'} Hedge Rule
          </Button>
        </ButtonGroup>
      </DrawerFooter>

      <Dialog
        {...hedgeRuleChangedWarningDialog}
        usePortal={true}
        portalID="drawer-portal"
        stretchButtons
        onConfirm={() => handleSubmitHedgeRule(AutoHedgerValidationLevel.POSITION_THRESHOLD_MODAL)}
        title="Threshold Warning"
        dataTestId="current-position-warning-dialog"
      >
        The current position{' '}
        <InlineFormattedNumber
          number={convertedPosition}
          currency={state.form.TargetAsset.value?.Symbol}
          round
          estimate={source === 'conversion'}
        />{' '}
        exceeds the Short/Long Threshold and the hedger will start hedging upon saving.
      </Dialog>
    </>
  );
}

const getAggregationLabel = (aggregation: Aggregation) => aggregation.DisplayName ?? aggregation.Name;
const getStrategyLabel = (strategy: OrderStrategy) => strategy.DisplayName ?? strategy.Name;
const getStrategyDescription = (strategy: OrderStrategy) => strategy.Description;

function getSubaccountGroup(item: ISubaccount) {
  return item.Type;
}

function getSubaccountLabel(item: ISubaccount) {
  return item.DisplayName;
}

export const LoadingOverlay = () => {
  const theme = useTheme();

  return (
    <LoaderTalos
      wrapperStyle={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        background: setAlpha(0.7, theme.colors.gray.main),
      }}
    />
  );
};

const ToastsWrapper = styled.div`
  position: absolute;
  bottom: 45px;
  padding: ${({ theme }) => `0 ${theme.spacing16}px ${theme.spacing16}px`};
  width: 100%;
  z-index: 3;
`;
